// src/components/MapBanner/MapBanner.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import { MapWrap, OverlayCard, PinTitle, Guard, Chip } from "./MapBannerStyles";

import L from "leaflet";
import "leaflet/dist/leaflet.css";

// fix pentru icons în bundlere
import marker2x from "leaflet/dist/images/marker-icon-2x.png";
import marker from "leaflet/dist/images/marker-icon.png";
import shadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: marker2x,
  iconUrl: marker,
  shadowUrl: shadow,
});

// praguri pentru a NU inițializa când containerul e 0x0 pe mobil
const MIN_W = 200;
const MIN_H = 160;

export default function MapBanner({ center, markerTitle }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const [active, setActive] = useState(false);
  const initTried = useRef(false);

  const disableInteractions = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.scrollWheelZoom.disable();
    mapRef.current.dragging.disable();
    mapRef.current.touchZoom.disable();
    mapRef.current.keyboard.disable();
    setActive(false);
  }, []);

  const enableInteractions = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.scrollWheelZoom.enable();
    mapRef.current.dragging.enable();
    mapRef.current.touchZoom.enable();
    mapRef.current.keyboard.enable();
    setActive(true);
  }, []);

  useEffect(() => {
    if (!mapEl.current) return;
    if (initTried.current && mapRef.current) return;

    const el = mapEl.current;

    const init = () => {
      if (initTried.current) return;
      initTried.current = true;

      const map = L.map(el, {
        zoomControl: false,
        scrollWheelZoom: false,
        dragging: false,
        touchZoom: false,
        keyboard: false,
        wheelPxPerZoomLevel: 180,
        wheelDebounceTime: 80,
        zoomSnap: 0.5,
      }).setView([center.lat, center.lng], 15);

      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
        {
          attribution: "&copy; OpenStreetMap & CARTO",
          detectRetina: true,
          maxZoom: 19,
          crossOrigin: true,
        },
      ).addTo(map);

      const goldIcon = new L.Icon({
        iconUrl:
          "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
        iconRetinaUrl:
          "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
        shadowUrl: shadow,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      });

      L.marker([center.lat, center.lng], { icon: goldIcon })
        .addTo(map)
        .bindPopup(markerTitle);

      mapRef.current = map;

      // recalibrare agresivă după mount & primul frame
      map.whenReady(() => {
        map.invalidateSize();
        requestAnimationFrame(() => map.invalidateSize());
        setTimeout(() => map.invalidateSize(), 200);
      });

      const onKey = (e) => {
        if (e.key === "Escape") disableInteractions();
      };
      window.addEventListener("keydown", onKey);

      const onOrientation = () => setTimeout(() => map.invalidateSize(), 120);
      window.addEventListener("orientationchange", onOrientation);

      // Cleanup
      return () => {
        window.removeEventListener("keydown", onKey);
        window.removeEventListener("orientationchange", onOrientation);
        map.remove();
        mapRef.current = null;
        initTried.current = false;
      };
    };

    // așteaptă până containerul are dimensiuni reale
    const tryInit = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width >= MIN_W && rect.height >= MIN_H) {
        cleanupRO();
        init();
      }
    };

    const ro = new ResizeObserver(tryInit);
    const cleanupRO = () => ro.disconnect?.();
    ro.observe(el);
    // încearcă imediat (poate e deja vizibil)
    tryInit();

    return () => {
      cleanupRO();
    };
  }, [center.lat, center.lng, markerTitle, disableInteractions]);

  return (
    <MapWrap
      onMouseLeave={() => {
        if (active) setTimeout(disableInteractions, 600);
      }}
    >
      {!active && (
        <Guard
          role="button"
          tabIndex={0}
          aria-label="Activează interacțiunea cu harta"
          onClick={enableInteractions}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && enableInteractions()
          }
        />
      )}

      {active && <Chip>Harta activă • ESC pentru a bloca</Chip>}

      <div ref={mapEl} className="map" />

      <OverlayCard>
        <PinTitle>{markerTitle}</PinTitle>
        <p>
          Ne găsești pe hartă în Predeal. Zoom & pan sunt disponibile după un
          click.
        </p>
      </OverlayCard>
    </MapWrap>
  );
}
